import { useState } from 'react';
import { Link } from 'react-router-dom';
import { courses, hospitalCourses } from '@/mocks/home';
import { fetchCourseList } from '@/services/course';
import { adaptCourses } from '@/services/adapters';
import { useRequest } from '@/hooks/useRequest';
import SectionHeading from '@/components/base/SectionHeading';
import CourseCard, { type CourseItem } from '@/components/feature/CourseCard';
import PurchaseQrModal from '@/components/feature/PurchaseQrModal';

interface CourseGroup {
  key: string;
  eyebrow: string;
  title: string;
  desc: string;
  to: string;
  items: CourseItem[];
}

export default function CoursesSection() {
  const [selected, setSelected] = useState<CourseItem | null>(null);

  // 接口失败时降级为 mock 数据；名师/名院两组按课程类型各取 4 门，单组请求失败仅该组回退 mock
  const fallback: [CourseItem[], CourseItem[]] = [courses.slice(0, 4), hospitalCourses.slice(0, 4)];
  const { data } = useRequest(
    async (): Promise<[CourseItem[], CourseItem[]]> => {
      const [master, hospital] = await Promise.allSettled([
        fetchCourseList({ pageIndex: 1, pageSize: 4, courseType: 1 }).then((res) => adaptCourses(res.list)),
        fetchCourseList({ pageIndex: 1, pageSize: 4, courseType: 2 }).then((res) => adaptCourses(res.list)),
      ]);
      return [
        master.status === 'fulfilled' ? master.value : fallback[0],
        hospital.status === 'fulfilled' ? hospital.value : fallback[1],
      ];
    },
    { fallback },
  );

  // 名师/名院两组均无数据时整段隐藏（单组为空时下方 filter 已隐藏该组）
  if (data[0].length === 0 && data[1].length === 0) return null;

  const groups: CourseGroup[] = [
    {
      key: 'master',
      eyebrow: '名师课程',
      title: '国医大师、名老中医亲授，传承临床真功夫',
      desc: '由国医大师、名老中医亲自授课，体系化拆解中医传承、辨证思路与临床经验。',
      to: '/courses/master',
      items: data[0],
    },
    {
      key: 'hospital',
      eyebrow: '名院课程',
      title: '三甲医院临床体系化课程，学以致用',
      desc: '联合三甲医院打造临床体系化课程，覆盖中西医结合、专科专病与实用技术。',
      to: '/courses/hospital',
      items: data[1],
    },
  ];

  return (
    <section id="courses" className="scroll-mt-24 w-full px-4 md:px-8 py-14 md:py-20">
      <div className="max-w-7xl mx-auto">
        <SectionHeading
          align="left"
          eyebrow="课程中心"
          title="名医名院课程、传承进阶赋能"
          className="mb-12 md:mb-16"
        />

        <div className="flex flex-col gap-14 md:gap-20">
          {groups
            .filter((group) => group.items.length > 0)
            .map((group) => (
            <div key={group.key} id={`courses-${group.key}`} className="scroll-mt-24">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                <div className="flex flex-col gap-3">
                  <span className="inline-flex items-center gap-2 self-start text-xs font-semibold tracking-wide text-primary-600">
                    <span className="w-6 h-px bg-primary-400"></span>
                    {group.eyebrow}
                  </span>
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 max-w-2xl text-balance">
                    {group.title}
                  </h3>
                  <p className="text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
                    {group.desc}
                  </p>
                </div>
                <Link
                  to={group.to}
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 px-4 py-2.5 rounded-md border border-primary-200 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap self-start md:self-end"
                >
                  查看全部{group.eyebrow}
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {group.items.slice(0, 4).map((course) => (
                  <CourseCard key={course.id} course={course} onBuy={setSelected} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <PurchaseQrModal
        open={selected !== null}
        onClose={() => setSelected(null)}
        courseTitle={selected?.title ?? ''}
        price={selected?.price ?? 0}
      />
    </section>
  );
}